import axios from 'axios';

// API temel URL'sini dinamik olarak belirle
const getApiBaseUrl = () => {
  const envBaseUrl = process.env.REACT_APP_API_BASE_URL?.trim();
  if (envBaseUrl) {
    return envBaseUrl.replace(/\/+$/, '');
  }

  // Production: IIS reverse proxy (/api -> localhost:5001) — aynı origin, SSL sorunu yok
  // Development: setupProxy.js -> 127.0.0.1:5001
  return '';
};

const API_BASE_URL = getApiBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // URL düzeltmesi - çift /api sorununu kesin olarak önle
    if (config.url) {
      // Production'da IIS reverse proxy kullanılıyor, baseURL zaten boş
      // Development'da da proxy kullanılıyor, baseURL boş
      if (!config.url.startsWith('http') && !config.url.startsWith('/api')) {
        // URL /api ile başlamıyorsa, /api ekle
        config.url = `/api${config.url.startsWith('/') ? config.url : `/${config.url}`}`;
      }
    }

    // DELETE metodu için özel konfigürasyon
    if (config.method === 'delete') {
      console.log('DELETE isteği konfigürasyonu:', {
        url: config.url,
        baseURL: config.baseURL,
        fullURL: `${config.baseURL}${config.url}`,
        method: config.method
      });
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    // Yeni token varsa güncelle
    const newToken = response.headers['x-new-token'];
    if (newToken) {
      localStorage.setItem('token', newToken);
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // DELETE metodu için detaylı hata bilgisi
    if (originalRequest.method === 'delete') {
      console.error('DELETE isteği hatası:', {
        status: error.response?.status,
        data: error.response?.data,
        headers: error.response?.headers
      });
    }

    // Token hatası kontrolü
    if (error.response?.status === 401) {
      if (error.response?.data?.code === 'TOKEN_EXPIRED' && !originalRequest._retry) {
        if (isRefreshing) {
          // Token yenileme işlemi devam ediyorsa, kuyruğa ekle
          try {
            const token = await new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            });
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          } catch (err) {
            return Promise.reject(err);
          }
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          // Token yenileme isteği
          const refreshToken = localStorage.getItem('refreshToken');
          if (!refreshToken) {
            throw new Error('Refresh token bulunamadı');
          }

          const response = await axios.post(`/api/users/refresh-token`, {
            refreshToken
          });

          const { token: newToken, refreshToken: newRefreshToken } = response.data;
          
          localStorage.setItem('token', newToken);
          localStorage.setItem('refreshToken', newRefreshToken);
          
          api.defaults.headers.common.Authorization = `Bearer ${newToken}`;
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          
          processQueue(null, newToken);
          return api(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          // Token yenileme başarısız - kullanıcıyı çıkış yap
          localStorage.clear();
          window.location.href = '/login?error=session_expired';
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      } else if (error.response?.data?.code === 'INVALID_TOKEN') {
        // Geçersiz token - kullanıcıyı çıkış yap
        localStorage.clear();
        window.location.href = '/login?error=invalid_token';
        return Promise.reject(error);
      }
    }

    // Rate limit hatası
    if (error.response?.status === 429) {
      const retryAfter = error.response.headers['retry-after'] || 60;
      return Promise.reject({
        ...error,
        message: `Çok fazla istek gönderildi. Lütfen ${retryAfter} saniye sonra tekrar deneyin.`
      });
    }

    // Sunucu hatası
    if (error.response?.status >= 500) {
      return Promise.reject({
        ...error,
        message: 'Sunucu hatası oluştu. Lütfen daha sonra tekrar deneyin.'
      });
    }

    return Promise.reject(error);
  }
);

export default api;
